import { createCensusProspect } from "@/api/census-account-client";
import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  pageBodyBlockBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { fetchCensusIdentity } from "@/hooks/use-census-session";
import { Eye, EyeOff, Lock, Mail, Phone, Store, User } from "@cakewalk/ui/icons";
import {
  V2Button,
  V2Card,
  V2CardContent,
  V2FormActions,
  V2FormField,
  V2InlineIconButton,
  V2Input,
  V2Typography,
} from "@cakewalk/ui/v2";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import { type FormEvent, useState } from "react";
import { OtpResend } from "./OtpResend";

/**
 * Prospect self-signup step for company-census.
 *
 * Reached when the email-first entry finds no record of the email. Unlike the
 * existing-owner create-account step, this provisions a BRAND-NEW company +
 * primary contact (role `prospect`) plus a Cognito login in the `prospect`
 * group only — so it collects name + business name (which names the created
 * company) + mobile phone (for SMS 2FA) + password.
 *
 * The company it creates is a LEAD, so the business step later overwrites this
 * name along with the rest of the profile (db-service `census-draft.service`).
 * That is why the field is safe to keep to a single, low-friction input here.
 *
 * After provisioning it signs the user in and confirms the SMS 2FA challenge
 * inline (same mechanics as the login / create-account steps), then hands off
 * to the census flow.
 */

const NAME_RE = /^[a-zA-Z\s\-'.]+$/;
/** Matches `organizations.name` — varchar(255). */
const ORG_NAME_MAX_LENGTH = 255;

/** Format phone keystrokes progressively into "(555) 123-4567". */
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function CompanyCensusProspectSignupStep({
  defaultEmail,
  playBuild = false,
  ceremony = null,
  onCreated,
  onBack,
}: {
  defaultEmail?: string;
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  onCreated: (email: string, canManage: boolean) => void;
  onBack: () => void;
}) {
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  // Which half of the submit we're in. Provisioning the company and signing in
  // are two separate round trips; naming the current one keeps the button
  // honest instead of showing "Creating your account…" through both.
  const [phase, setPhase] = useState<"provisioning" | "signing-in">("provisioning");
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const [awaitingOtp, setAwaitingOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [maskedDestination, setMaskedDestination] = useState<string | undefined>(undefined);

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  const phoneDigits = phone.replace(/\D/g, "").length;
  const canSubmit =
    email.trim().length > 0 &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    companyName.trim().length > 0 &&
    phoneDigits === 10 &&
    password.length > 0;

  const finishLogin = async () => {
    const identity = await fetchCensusIdentity();
    onCreated(email.trim(), identity.canManage);
  };

  const startSignIn = async () => {
    const response = await signIn({ username: email.trim(), password });
    if (response.isSignedIn) {
      await finishLogin();
      return;
    }
    const step = response.nextStep?.signInStep;
    if (step === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
      setMaskedDestination(response.nextStep.codeDeliveryDetails?.destination);
      setAwaitingOtp(true);
      return;
    }
    setNotice(
      "Your account is ready. Finish setting up two-factor on the main Cakewalk app, then come back and log in.",
    );
  };

  // Re-send the SMS by restarting sign-in (the account already exists, so this
  // never re-provisions). <OtpResend> owns the cooldown.
  const resendCode = async () => {
    setError(undefined);
    try {
      const response = await signIn({ username: email.trim(), password });
      if (response.isSignedIn) {
        await finishLogin();
        return;
      }
      if (response.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
        setMaskedDestination(response.nextStep.codeDeliveryDetails?.destination);
        setOtp("");
        return;
      }
      setError("We couldn't resend the code. Try again.");
      throw new Error(`resend-unexpected-step:${response.nextStep?.signInStep ?? "unknown"}`);
    } catch (err) {
      if (err instanceof Error && err.name === "UserAlreadyAuthenticatedException") {
        await finishLogin();
        return;
      }
      setError((prev) => prev ?? (err instanceof Error ? err.message : "Failed to resend code"));
      throw err;
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading || !canSubmit) return;
    if (!NAME_RE.test(firstName.trim()) || !NAME_RE.test(lastName.trim())) {
      setError("Enter your first and last name using letters only.");
      return;
    }
    setIsLoading(true);
    setPhase("provisioning");
    setError(undefined);
    setNotice(undefined);
    try {
      await createCensusProspect({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        companyName: companyName.trim(),
        phone,
        password,
      });
      // The account exists; the remaining wait is Cognito minting the session
      // and texting the code. Say so rather than leaving "Creating your
      // account…" on screen for the whole round trip.
      setPhase("signing-in");
      await startSignIn();
    } catch (err) {
      if (err instanceof Error && err.name === "UserAlreadyAuthenticatedException") {
        await finishLogin();
        return;
      }
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await confirmSignIn({ challengeResponse: otp.replace(/\D/g, "") });
      if (result.isSignedIn) {
        await finishLogin();
        return;
      }
      setError(`Authentication incomplete (${result.nextStep?.signInStep ?? "UNKNOWN_STEP"}).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to verify code");
    } finally {
      setIsLoading(false);
    }
  };

  if (awaitingOtp) {
    return (
      // Key the OTP screen so it mounts a FRESH subtree (keeps the browser's SMS
      // one-time-code suggestion working — same fix as the other auth steps).
      <div key="otp" className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
        <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
          <V2CardContent className="flex flex-col gap-6 p-6">
            <div className="flex flex-col gap-2">
              <V2Typography variant="overline" className="text-cw-v2-reliant-red">
                Verify it&rsquo;s you
              </V2Typography>
              <V2Typography variant="heading-medium" as="h1" className="font-semibold">
                Enter your verification code
              </V2Typography>
              <V2Typography variant="body" muted>
                We sent a code to {maskedDestination ?? "your phone"}. Enter it below to finish
                creating your account.
              </V2Typography>
            </div>

            <form onSubmit={handleOtpSubmit} className="flex flex-col gap-5">
              <V2FormField label="Verification code" error={error}>
                <V2Input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  data-sensitive
                />
              </V2FormField>
              <V2FormActions variant="stretch">
                <V2Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={isLoading || otp.replace(/\D/g, "").length === 0}
                  data-track="button-company-census-prospect-verify"
                >
                  Verify and continue
                </V2Button>
              </V2FormActions>
            </form>

            <div className="flex justify-center">
              <OtpResend onResend={resendCode} dataTrack="button-company-census-prospect-resend" />
            </div>
          </V2CardContent>
        </V2Card>
      </div>
    );
  }

  return (
    <div key="prospect-signup" className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
      <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
        <V2CardContent className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-2">
            <V2Typography
              variant="overline"
              {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
            >
              Create your account
            </V2Typography>
            <V2Typography
              variant="heading-medium"
              as="h1"
              {...mergeStepBuildProps(titleMotion, { className: "font-semibold" })}
            >
              Set up your login
            </V2Typography>
            <V2Typography variant="body" muted {...mergeStepBuildProps(descriptionMotion)}>
              We couldn&rsquo;t find an account for that email, so let&rsquo;s make one. We&rsquo;ll
              text your two-factor code to the mobile number you enter.
            </V2Typography>
          </div>

          {notice ? (
            <V2Typography variant="body" className="text-cw-v2-reliant-red">
              {notice}
            </V2Typography>
          ) : null}

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5"
            data-track="form-company-census-prospect-signup"
          >
            <div
              className="grid gap-4 sm:grid-cols-2"
              {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 0, ceremony))}
            >
              <V2FormField label="First name">
                <V2Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  placeholder="Jordan"
                  prefix={<User className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>
              <V2FormField label="Last name">
                <V2Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  placeholder="Rivera"
                  data-sensitive
                />
              </V2FormField>
            </div>

            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 1, ceremony))}>
              <V2FormField label="Business name">
                <V2Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  autoComplete="organization"
                  placeholder="Acme Coffee Roasters"
                  maxLength={ORG_NAME_MAX_LENGTH}
                  prefix={<Store className="h-4 w-4" aria-hidden />}
                />
              </V2FormField>
              <V2Typography variant="meta" muted className="mt-1">
                This names your company on the quote — you can refine it later.
              </V2Typography>
            </div>

            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 2, ceremony))}>
              <V2FormField label="Email">
                <V2Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  prefix={<Mail className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>
            </div>

            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 3, ceremony))}>
              <V2FormField label="Mobile phone">
                <V2Input
                  value={formatPhone(phone)}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(555) 123-4567"
                  maxLength={14}
                  prefix={<Phone className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>
              <V2Typography variant="meta" muted className="mt-1">
                We&rsquo;ll text your two-factor code here.
              </V2Typography>
            </div>

            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 4, ceremony))}>
              <V2FormField label="Password" error={error}>
                <V2Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  prefix={<Lock className="h-4 w-4" aria-hidden />}
                  suffix={
                    <V2InlineIconButton
                      type="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword((v) => !v)}
                      icon={
                        showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />
                      }
                    />
                  }
                  data-sensitive
                />
              </V2FormField>
            </div>

            <V2FormActions
              variant="stretch"
              {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 5, ceremony))}
            >
              <V2Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={isLoading || !canSubmit}
                data-track="button-company-census-prospect-submit"
              >
                {isLoading
                  ? phase === "provisioning"
                    ? "Creating your account…"
                    : "Texting your code…"
                  : "Create account"}
              </V2Button>
            </V2FormActions>
          </form>

          <div className="flex flex-col items-center gap-1 border-t border-cw-v2-border/60 pt-5">
            <V2Typography variant="meta" muted>
              Already have an account?
            </V2Typography>
            <V2Button
              variant="link"
              onClick={onBack}
              data-track="button-company-census-prospect-back-to-login"
            >
              Back to login
            </V2Button>
          </div>
        </V2CardContent>
      </V2Card>

      <div className="flex items-center justify-center gap-2 text-cw-v2-text-muted">
        <Lock className="h-3.5 w-3.5" aria-hidden />
        <V2Typography variant="meta" muted>
          Encrypted &amp; secure. Your login works across Cakewalk.
        </V2Typography>
      </div>
    </div>
  );
}
